import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Divider,
  Stack,
  alpha
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  Schedule as ScheduleIcon,
  Notes as NotesIcon,
  Assignment as AssignmentIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { api } from '@/services/api';
import type { 
  ApplicationResponse,
  CreateApplicationRequest,
  OpportunityDetailResponse
} from '@/types/models';
import { ROUTES } from '@/config/routes';
import { OpportunityApi } from '@/services/api/domains/opportunity';
import { useToast } from '@/components/common/Toast';

const getStatusColor = (status: string): "success" | "warning" | "error" | "info" => {
  switch (status.toLowerCase()) {
    case 'approved':
      return 'success';
    case 'pending':
      return 'warning';
    case 'rejected':
      return 'error';
    default:
      return 'info';
  }
};

const InfoItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}> = ({ icon, label, value }) => (
  <Stack spacing={0.5}>
    <Typography variant="overline" color="text.secondary">
      {label}
    </Typography>
    <Stack direction="row" alignItems="center" spacing={1}>
      {icon}
      {typeof value === 'string' ? (
        <Typography variant="body1">{value}</Typography>
      ) : (
        value
      )}
    </Stack>
  </Stack>
);

export const ApplicationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [studentNote, setStudentNote] = React.useState('');
  const [coverLetter, setCoverLetter] = React.useState('');
  const toast = useToast();
  const [isPending] = useState(false);

  // Check if this is a new application
  const isNewApplication = location.pathname.includes('/new/');
  const opportunityId = isNewApplication ? id : null;

  // Query for existing application
  const { data: application, isLoading: isLoadingApplication } = useQuery<ApplicationResponse>({
    queryKey: ['application', id],
    queryFn: () => api.applications.getApplicationDetail(Number(id)),
    enabled: !!id && !isNewApplication,
  });

  // Query for opportunity details (for new applications)
  const { data: opportunity, isLoading: isLoadingOpportunity } = useQuery<OpportunityDetailResponse>({
    queryKey: OpportunityApi.getOpportunityQueryKey(opportunityId),
    queryFn: async () => {
      if (!opportunityId) {
        throw new Error('No opportunity ID provided');
      }
      return api.opportunities.getOpportunityDetail(Number(opportunityId));
    },
    enabled: !!opportunityId && isNewApplication
  });

  // For existing applications, fetch the full opportunity details using the opportunity ID from the application
  const { data: applicationOpportunity, isLoading: isLoadingApplicationOpportunity } = useQuery<OpportunityDetailResponse>({
    queryKey: OpportunityApi.getOpportunityQueryKey(application?.opportunity?.id),
    queryFn: async () => {
      if (!application?.opportunity?.id) {
        throw new Error('No opportunity ID found in application');
      }
      return api.opportunities.getOpportunityDetail(application.opportunity.id);
    },
    enabled: !isNewApplication && !!application?.opportunity?.id
  });

  const createApplication = useMutation({
    mutationFn: (data: CreateApplicationRequest) => api.applications.createApplication(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      // Also invalidate the application check cache
      if (opportunityId) {
        queryClient.invalidateQueries({ 
          queryKey: OpportunityApi.getApplicationCheckQueryKey(opportunityId)
        });
      }
      navigate(ROUTES.STUDENT.APPLICATIONS.DETAIL(response.id));
    }
  });

  const handleSubmit = async () => {
    try {
      if (!application?.cover_letter) {
        toast.showError('Please provide a cover letter');
        return;
      }
      await createApplication.mutateAsync({
        opportunity_id: Number(id),
        student_note: application.student_note || '',
        cover_letter: application.cover_letter,
      });
      toast.showSuccess('Application submitted successfully!');
      navigate('/student/applications');
    } catch (err) {
      toast.showError('Failed to submit application');
      console.error('Error submitting application:', err);
    }
  };

  const handleBack = () => {
    if (isNewApplication && opportunityId) {
      navigate(ROUTES.STUDENT.OPPORTUNITIES.DETAIL(opportunityId));
    } else {
      navigate(ROUTES.STUDENT.APPLICATIONS.LIST);
    }
  };

  const isLoading = isLoadingApplication || isLoadingOpportunity || isLoadingApplicationOpportunity;
  
  // Use the appropriate opportunity data based on the context
  const displayData = isNewApplication ? opportunity : applicationOpportunity;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!displayData || !displayData.organization || typeof displayData.organization === 'string') {
    return (
      <Alert severity="error">
        Failed to load opportunity details. Please try again later.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: -3 }}>
      {/* Header Section */}
      <Box 
        sx={{ 
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        {/* Back button */}
        <Button
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          size="small"
          sx={{
            alignSelf: 'flex-start',
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main',
              backgroundColor: 'transparent'
            }
          }}
        >
          Back
        </Button>

        {/* Opportunity Title */}
        <Typography 
          variant="h4" 
          component="h1"
          sx={{ 
            fontWeight: 600,
            fontSize: { xs: '1.5rem', sm: '1.75rem' },
            lineHeight: 1.2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'text.primary'
          }}
        >
          <DescriptionIcon 
            sx={{ 
              fontSize: '1.3em',
              color: 'primary.main'
            }} 
          />
          {displayData.title}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Stack spacing={2}>
            {/* Application Details */}
            {!isNewApplication && application && (
              <>
                {/* Cover Letter */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: theme => alpha(theme.palette.background.paper, 0.8),
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                    <AssignmentIcon color="primary" />
                    <Typography variant="h6">
                      Cover Letter
                    </Typography>
                  </Stack>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {application.cover_letter}
                  </Typography>
                </Paper>

                {/* Additional Notes */}
                {application.student_note && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: theme => alpha(theme.palette.background.paper, 0.8),
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                      <NotesIcon color="primary" />
                      <Typography variant="h6">
                        Additional Notes
                      </Typography>
                    </Stack>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {application.student_note}
                    </Typography>
                  </Paper>
                )}
              </>
            )}

            {/* New Application Form */}
            {isNewApplication && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: theme => alpha(theme.palette.background.paper, 0.8),
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                  <AssignmentIcon color="primary" />
                  <Typography variant="h6">
                    Submit Your Application
                  </Typography>
                </Stack>

                <form onSubmit={handleSubmit}>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                        Cover Letter
                      </Typography>
                      <TextField
                        multiline
                        rows={6}
                        fullWidth
                        value={coverLetter}
                        onChange={(e) => setCoverLetter(e.target.value)}
                        placeholder="Write your cover letter here..."
                        required
                      />
                    </Box>

                    <Box>
                      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                        Additional Notes
                      </Typography>
                      <TextField
                        multiline
                        rows={4}
                        fullWidth
                        value={studentNote}
                        onChange={(e) => setStudentNote(e.target.value)}
                        placeholder="Any additional notes or questions..."
                      />
                    </Box>

                    <Box display="flex" gap={2} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        onClick={handleBack}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        type="submit"
                        disabled={isPending}
                      >
                        {isPending ? 'Submitting...' : 'Submit Application'}
                      </Button>
                    </Box>
                  </Stack>
                </form>
              </Paper>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            {/* Application History */}
            {!isNewApplication && application && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: theme => alpha(theme.palette.background.paper, 0.8),
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Application History
                </Typography>
                <Stack spacing={1.5}>
                  <InfoItem
                    icon={<CalendarIcon color="action" />}
                    label="Applied On"
                    value={new Date(application.created_at).toLocaleDateString()}
                  />
                  <Divider />
                  <InfoItem
                    icon={<AssignmentIcon color="action" />}
                    label="Current Status"
                    value={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={application.status}
                          color={getStatusColor(application.status)}
                          size="small"
                        />
                        {application.reviewed_at && (
                          <Typography variant="body2" color="text.secondary">
                            (Reviewed on {new Date(application.reviewed_at).toLocaleDateString()})
                          </Typography>
                        )}
                      </Stack>
                    }
                  />
                </Stack>
              </Paper>
            )}

            {/* Opportunity Details */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                bgcolor: theme => alpha(theme.palette.background.paper, 0.8),
              }}
            >
              <Typography variant="h6" gutterBottom>
                Opportunity Details
              </Typography>
              <Stack spacing={1.5}>
                <InfoItem
                  icon={<BusinessIcon color="action" />}
                  label="Organization"
                  value={displayData.organization.name}
                />
                <Divider />
                <InfoItem
                  icon={<LocationIcon color="action" />}
                  label="Location"
                  value={displayData.location_type}
                />
                <Divider />
                <InfoItem
                  icon={<AccessTimeIcon color="action" />}
                  label="Commitment"
                  value={`${displayData.commitment_hours} hours`}
                />
                {(displayData.start_date || displayData.end_date) && (
                  <>
                    <Divider />
                    <InfoItem
                      icon={<CalendarIcon color="action" />}
                      label="Dates"
                      value={
                        <>
                          {displayData.start_date && new Date(displayData.start_date).toLocaleDateString()}
                          {displayData.end_date && ` - ${new Date(displayData.end_date).toLocaleDateString()}`}
                        </>
                      }
                    />
                  </>
                )}
                {displayData.schedule_description && (
                  <>
                    <Divider />
                    <InfoItem
                      icon={<ScheduleIcon color="action" />}
                      label="Schedule"
                      value={displayData.schedule_description}
                    />
                  </>
                )}
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}; 